import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { deserializer } from '@shared/helpers/deserializer.helper';
import { errorHandler } from '@shared/helpers/handle-error.helper';
import { INotification } from '@shared/interfaces/notification.interface';
import { IPaginable } from '@shared/interfaces/paginable.interface';
import { Notification } from '@shared/models/notification.model';
import { NotificationsSerializer } from '@shared/serializers/notifications.serializer';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService implements IPaginable {
  private readonly endpoint: string = String(
    `${this._commonEnvironments.API_ENDPOINT}/notifications`
  );

  constructor(
    private readonly _commonEnvironments: CommonEnvironmentsService,
    private readonly _http: HttpClient
  ) { }

  public delete(id: string): Observable<INotification> {
    return this._http.delete(`${this.endpoint}/${id}`).pipe(
      map((res: any) => new Notification(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }

  public find(id: string): Observable<INotification> {
    return this._http.get(`${this.endpoint}/${id}`).pipe(
      map((res: any) => new Notification(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }

  public get(
    pageNumber?: number,
    filters?: any,
    onGetRawData?: (data: any) => void
  ): Observable<INotification[]> {
    let httpParams = new HttpParams();

    if (pageNumber) {
      httpParams = httpParams.set('page[number]', String(pageNumber));
    }

    if (filters) {
      filters.map((f) => {
        if (f.value) {
          httpParams = httpParams.append(f.key, f.value);
        }
      });
    }

    return this._http.get(this.endpoint, { params: httpParams }).pipe(
      tap((data: any) => onGetRawData && onGetRawData(data)),
      map((res: any) =>
        deserializer.deserialize(res).map((e: INotification) => new Notification(e))
      ),
      catchError(errorHandler)
    );
  }

  public patch(e: Partial<INotification>): Observable<INotification> {
    const data = NotificationsSerializer.serialize(e);
    return this._http.patch(`${this.endpoint}/${e.id}`, data).pipe(
      map((res: JSON) => new Notification(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }

  public send(action: Partial<INotification>): Observable<INotification> {
    const data = NotificationsSerializer.serialize(action);
    return this._http.post(`${this.endpoint}/send`, data).pipe(
      map((res: any) => new Notification(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }

  public post(action: Partial<INotification>): Observable<INotification> {
    const data = NotificationsSerializer.serialize(action);
    return this._http.post(this.endpoint, data).pipe(
      map((res: any) => new Notification(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }
}
